import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { RatingGroup } from '../components/RatingGroup';
import { TechStackSection } from '../components/TechStackSection';
import { NavigationControls } from '../components/NavigationControls';
import { SectionRecapCard } from '../components/SectionRecapCard';
import { Settings, Folder, Clock, DollarSign, Lightbulb } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';

const questions = [
  'We have manual, repetitive tasks that could be automated.',
  'Our systems and tools don\'t communicate with each other.',
  'We re-enter the same data multiple times across different platforms.',
  'Important updates or information get lost in email threads.',
  'Critical knowledge leaves when an employee leaves.'
];

export default function OperationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditData, updateSectionRating, updateSectionNotes } = useAudit();
  const section = auditData.sections.operations;
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  const validateReflection = () => {
    const notes = section.notes || '';
    if (notes.trim().length < 50) {
      setShowValidationDialog(true);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateReflection()) {
      navigate(auditPath('/customer', location.pathname));
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  return (
    <PageFrame pageNumber={3} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header bar */}
          <div className="bg-primary text-white rounded-lg p-6 text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide w-full sm:w-auto mx-auto" data-testid="section-title">
              Section 1 – Operations & Efficiency
            </h1>
          </div>

          {/* Questions */}
          <SectionCard
            title="Rate Each Statement"
            description="How true is each statement for your business?"
            icon={Settings}
          >
            <div className="space-y-8">
              {questions.map((question, index) => (
                <div key={index} className="pb-6 border-b border-muted last:border-0">
                  <RatingGroup
                    value={section.ratings[index]}
                    onChange={(rating) => updateSectionRating('operations', index, rating)}
                    questionId={`operations-q${index + 1}`}
                    label={`${index + 1}. ${question}`}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Score Display */}
          <div className="flex items-center justify-between bg-muted rounded-lg p-4 mt-4">
            <span className="text-secondary font-semibold">Section Total:</span>
            <span className="text-3xl font-bold text-primary" data-testid="operations-total">
              {section.total} <span className="text-base text-muted-foreground">/ 25</span>
            </span>
          </div>

          <SectionRecapCard
            sectionKey="operations"
            section={section}
            tools={auditData.tech_stack?.operations?.tools || []}
            integrationRating={auditData.tech_stack?.operations?.integration_rating || 0}
          />

          {/* Insight Box */}
          <div className="bg-primary text-white rounded-lg p-6 mt-6">
            <div className="flex items-start gap-4">
              <Lightbulb className="h-8 w-8 flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-bold text-lg mb-2">Insight</h3>
                <p>
                  High scores here indicate strong opportunities for workflow automation, intelligent document processing, and knowledge management systems.
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <SectionCard title="Reflection" icon={Folder} className="mt-6">
            <div className="space-y-2">
              <Label htmlFor="operations-notes">What operational challenge frustrates you most? (Minimum 50 characters required)</Label>
              <Textarea
                id="operations-notes"
                value={section.notes}
                onChange={(e) => updateSectionNotes('operations', e.target.value)}
                placeholder="Share your thoughts here... (minimum 50 characters)"
                className="min-h-32 resize-y"
                data-testid="operations-notes-textarea"
              />
              <p className="text-xs text-muted-foreground">{section.notes?.length || 0} / 50 characters</p>
            </div>
          </SectionCard>

          {/* Tech Stack Section */}
          <div className="mt-6">
            <TechStackSection category="operations" />
          </div>

          {/* Navigation with custom Next */}
          <div className="pt-4">
            <NavigationControls currentPage={3} customNext={handleNext} />
          </div>

          {/* Validation Dialog */}
          <AlertDialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reflection Required</AlertDialogTitle>
                <AlertDialogDescription>
                  Please share your thoughts about your operational challenges (at least 50 characters). This helps us generate a more personalized and valuable report for you.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Button onClick={() => setShowValidationDialog(false)}>
                  Got it
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </PageFrame>
  );
}